import type { MeasurementFreshness, FrameTimingSource } from './streamRecovery/contracts';
/**
 * Locked contracts for WebRTC latency recovery + stream quality v2.
 * Constants and message shapes only — no runtime recovery behavior here.
 */

import { isQuestionableConnection, type NetworkConnectionHint } from './connectionQualityProbe';
import type { LatencyProbeSource } from './latencyProbeContracts';
import { SHARED_AUDIO_JITTER_BUFFER_TARGET_MS } from './playoutDelay';

/** Video stays on render-ASAP; audio keeps one shared NetEq jitter floor. */
export const VIDEO_JITTER_BUFFER_TARGET_MS = 0;
export const AUDIO_JITTER_BUFFER_TARGET_MS = SHARED_AUDIO_JITTER_BUFFER_TARGET_MS;

export function jitterBufferTargetForMediaKind(kind: 'audio' | 'video'): number {
  return kind === 'audio' ? AUDIO_JITTER_BUFFER_TARGET_MS : VIDEO_JITTER_BUFFER_TARGET_MS;
}

/** Dual-phase startup burst window (ms). */
export const STARTUP_BURST_MS = 5_000;

/** Desktop burst profile (bps) — first STARTUP_BURST_MS. */
export const DESKTOP_BURST_MAX_BITRATE_BPS = 10_000_000;
export const DESKTOP_BURST_START_BITRATE_BPS = 7_000_000;
export const DESKTOP_BURST_MIN_BITRATE_BPS = 3_500_000;

/** Desktop settle profile (bps) — after burst. */
export const DESKTOP_MAX_BITRATE_BPS = 8_000_000;
export const DESKTOP_START_BITRATE_BPS = 3_000_000;
export const DESKTOP_MIN_BITRATE_BPS = 500_000;

/** Mobile burst profile (bps). */
export const MOBILE_BURST_MAX_BITRATE_BPS = 6_000_000;
export const MOBILE_BURST_START_BITRATE_BPS = 3_000_000;
export const MOBILE_BURST_MIN_BITRATE_BPS = 1_500_000;

/** Mobile settle profile (bps). */
export const MOBILE_MAX_BITRATE_BPS = 8_000_000;
export const MOBILE_START_BITRATE_BPS = 2_000_000;
export const MOBILE_MIN_BITRATE_BPS = 500_000;

/**
 * Mobile audio: 128k stereo Opus is heavy for a cell radio and buys nothing on a phone
 * speaker. When MOBILE_AUDIO_DEGRADATION is ON the guest offers mono + the capped bitrate
 * below instead of stereo + maxaveragebitrate 128k. WebKit ignores modified fmtp on the
 * offer side, so the visible effect on Safari is capped by WebRTC's own opus rate control
 * when the sender honours the receive cap; on Chromium/Android the fmtp is honoured.
 *
 * PRODUCT DECISION — currently OFF (unchanged behaviour). Turn on to shed ~64k Mbps per
 * mobile guest and the stereo→mono encode cost on weak hosts.
 */
export const MOBILE_AUDIO_DEGRADATION = false;
/** Mono, lower-bitrate Opus for the mobile audio degradation profile. */
export const MOBILE_AUDIO_MAX_BITRATE_BPS = 64_000;

export type BitratePhase = 'burst' | 'settle';

export type BitrateProfile = {
  maxBitrate: number;
  startBitrate: number;
  minBitrate: number;
};

export function bitrateProfileFor(isMobile: boolean, phase: BitratePhase): BitrateProfile {
  if (isMobile) {
    if (phase === 'burst') {
      return {
        maxBitrate: MOBILE_BURST_MAX_BITRATE_BPS,
        startBitrate: MOBILE_BURST_START_BITRATE_BPS,
        minBitrate: MOBILE_BURST_MIN_BITRATE_BPS,
      };
    }
    return {
      maxBitrate: MOBILE_MAX_BITRATE_BPS,
      startBitrate: MOBILE_START_BITRATE_BPS,
      minBitrate: MOBILE_MIN_BITRATE_BPS,
    };
  }
  if (phase === 'burst') {
    return {
      maxBitrate: DESKTOP_BURST_MAX_BITRATE_BPS,
      startBitrate: DESKTOP_BURST_START_BITRATE_BPS,
      minBitrate: DESKTOP_BURST_MIN_BITRATE_BPS,
    };
  }
  return {
    maxBitrate: DESKTOP_MAX_BITRATE_BPS,
    startBitrate: DESKTOP_START_BITRATE_BPS,
    minBitrate: DESKTOP_MIN_BITRATE_BPS,
  };
}

/**
 * Gaming-path degradation preference (stream quality v2).
 *
 * `maintain-resolution` pins the encoder to the ShadowCam capture resolution: when
 * bandwidth or CPU runs short, Chromium lowers framerate instead of stepping the
 * picture down and back up. Do NOT switch this to `maintain-framerate` — that hands
 * Chromium's quality scaler permission to change resolution mid-stream, which reads
 * as the picture jumping between sharp and soft.
 */
export const VIDEO_DEGRADATION_PREFERENCE = 'maintain-resolution' as const;

/**
 * Resolution is pinned for the whole session: whatever the ShadowCam capture profile
 * produces is what the encoder sends. Every sender-parameter write asserts this value
 * so no code path — congestion recovery included — can introduce a resolution step.
 */
export const PINNED_SCALE_RESOLUTION_DOWN_BY = 1;

export type StreamResolution = {
  width: number;
  height: number;
};

/** Locked capture/encode resolution for a quality tier. */
export type QualityTier = 'top' | 'middle' | 'bottom';

/**
 * Per-tier capture locks (ShadowCam mapping is part of the contract).
 * Resolution is chosen once at session start (4C): no mid-session ShadowCam switch
 * and no scaleResolutionDownBy-based tier change — recovery is fps+bitrate only.
 */
export const QUALITY_TIER_RESOLUTION = {
  /** ShadowCam03 — desktop initial (decision 3B / 4C). */
  top: { width: 1280, height: 720 },
  /** ShadowCam01 — mobile when connection is not questionable (1B). */
  middle: { width: 896, height: 480 },
  /** ShadowCam00 — mobile when connection is questionable (1B). */
  bottom: { width: 640, height: 360 },
} as const satisfies Record<QualityTier, StreamResolution>;

export const QUALITY_TIER_SHADOWCAM: Record<
  QualityTier,
  'ShadowCam03' | 'ShadowCam01' | 'ShadowCam00'
> = {
  top: 'ShadowCam03',
  middle: 'ShadowCam01',
  bottom: 'ShadowCam00',
};

/**
 * The pinned resolution each device class streams at. Because resolution never moves
 * after capture (see PINNED_SCALE_RESOLUTION_DOWN_BY), these are also the exact numbers
 * the hardware-capability probe should ask encoders and decoders about.
 *
 * Aliases of QUALITY_TIER_RESOLUTION middle/top so existing callers keep compiling
 * until Phase 2/4 migrate to tier keys.
 */
export const STREAM_RESOLUTION_BY_DEVICE = {
  /** ShadowCam01 — alias of QUALITY_TIER_RESOLUTION.middle. */
  mobile: QUALITY_TIER_RESOLUTION.middle,
  /** ShadowCam03 — alias of QUALITY_TIER_RESOLUTION.top. */
  desktop: QUALITY_TIER_RESOLUTION.top,
} as const satisfies Record<string, StreamResolution>;

export function streamResolutionFor(isMobile: boolean): StreamResolution {
  return isMobile
    ? STREAM_RESOLUTION_BY_DEVICE.mobile
    : STREAM_RESOLUTION_BY_DEVICE.desktop;
}

/**
 * Fallback sender frame rate when the application-selected camera rate cannot be
 * opened. A live capture replaces this with that camera's rate.
 */
export const TARGET_FRAMERATE = 60;

/**
 * Frame rate the sender should use. The captured camera rate wins. A session
 * target below the fallback is an explicit limit and still caps the camera rate.
 */
export function senderFrameRateForCapture(
  capturedFps: number | null | undefined,
  sessionTargetFps: number | undefined,
): number {
  const session = sessionTargetFps && sessionTargetFps > 0 ? sessionTargetFps : TARGET_FRAMERATE;
  if (capturedFps == null || !(capturedFps > 0) || !Number.isFinite(capturedFps)) return session;
  if (session < TARGET_FRAMERATE) return Math.min(capturedFps, session);
  return capturedFps;
}

/**
 * Connection quality inputs for the initial tier (decision 2B) live in
 * ./connectionQualityProbe — the Network Information API is only one of the signals and
 * cannot stand alone (Safari does not implement it). Re-exported here so existing tier
 * callers keep a single import.
 */
export {
  QUESTIONABLE_DOWNLINK_MBPS,
  QUESTIONABLE_NETINFO_RTT_MS,
  isQuestionableConnection,
} from './connectionQualityProbe';
export type { NetworkConnectionHint } from './connectionQualityProbe';

export type InitialTierInput = {
  isMobile?: boolean;
  /** Precomputed guest hint; if omitted, evaluate `connection` via isQuestionableConnection. */
  connectionQuestionable?: boolean;
  connection?: NetworkConnectionHint | null;
};

export type InitialQualitySelection = {
  tier: QualityTier;
  shadowcam: 'ShadowCam03' | 'ShadowCam01' | 'ShadowCam00';
  resolution: StreamResolution;
  targetFramerate: typeof TARGET_FRAMERATE;
};

/** desktop → top; mobile good → middle; mobile questionable → bottom. */
export function selectInitialQualityTier(
  input: InitialTierInput,
): InitialQualitySelection {
  const isMobile = input.isMobile === true;
  const questionable =
    input.connectionQuestionable !== undefined
      ? input.connectionQuestionable
      : isQuestionableConnection(input.connection);

  const tier: QualityTier = !isMobile
    ? 'top'
    : questionable
      ? 'bottom'
      : 'middle';

  return {
    tier,
    shadowcam: QUALITY_TIER_SHADOWCAM[tier],
    resolution: QUALITY_TIER_RESOLUTION[tier],
    targetFramerate: TARGET_FRAMERATE,
  };
}

/**
 * How many times the host may renegotiate toward hardware. ZERO, deliberately.
 *
 * The renegotiate re-applied the SAME codec preferences and restarted ICE, which cannot
 * change which encoder Chromium picks — the preference order is the lever, and it is
 * already applied at connection setup. Worse, it produced a reconnect loop: the ICE restart
 * looked like transport loss to the guest, the guest rebuilt the connection, the rebuild
 * created a fresh controller with a fresh escalation budget, and ten seconds later it
 * restarted ICE again.
 *
 * Detection is now report-only. Reaching hardware is decided by profile ordering at
 * negotiation time (selectHardwareH264Profiles); if that did not achieve it, disrupting a
 * running session cannot either.
 */
export const SOFTWARE_ENCODE_MAX_ESCALATIONS = 0;

/**
 * Consecutive 1Hz polls that must report software before the host acts.
 *
 * `encoderImplementation` is not trustworthy the instant a connection comes up. Chromium
 * wraps hardware encoders in a fallback shim and can report the software name while the
 * hardware encoder is still initialising, so a host with a perfectly good GPU encoder
 * reports OpenH264 for the first seconds of every session. Acting on the first sample turned
 * that into an ICE-restart storm followed by a refused session.
 */
export const SOFTWARE_ENCODE_CONFIRM_SAMPLES = 10;

export type SoftwareEncodePolicy = {
  awaitPreferHardwareBeforeOffer: true;
  maxHardwareEscalations: number;
  /**
   * Whether to END the session when hardware encode cannot be reached.
   *
   * Strict hardware-only sessions terminate after the bounded software confirmation window.
   */
  endSessionWhenHardwareUnreachable: boolean;
};

export const SOFTWARE_ENCODE_POLICY: SoftwareEncodePolicy = {
  awaitPreferHardwareBeforeOffer: true,
  maxHardwareEscalations: SOFTWARE_ENCODE_MAX_ESCALATIONS,
  endSessionWhenHardwareUnreachable: true,
};

/** Baseline decode FPS floor before impairment. */
export const SLO_BASELINE_DECODE_FPS_MIN = 55;
/** After impairment cleared + recovery, decode FPS floor. */
export const SLO_POST_CLEAR_DECODE_FPS_MIN = 45;
/** After impairment cleared, input→photon p95 ceiling (ms, lab localhost). */
export const SLO_POST_CLEAR_INPUT_TO_PHOTON_P95_MS = 150;

export const CONGESTION_MESSAGE_TYPE = 'congestion' as const;

export type CongestionLevel = 'elevated' | 'critical';

export type CongestionMessage = {
  type: typeof CONGESTION_MESSAGE_TYPE;
  /** elevated = early warn; critical = hard cut now */
  level: CongestionLevel;
  rttMs?: number;
  jbMs?: number;
};

export function isCongestionMessage(value: unknown): value is CongestionMessage {
  if (value === null || typeof value !== 'object') return false;
  const msg = value as Record<string, unknown>;
  if (msg['type'] !== CONGESTION_MESSAGE_TYPE) return false;
  if (msg['level'] !== 'elevated' && msg['level'] !== 'critical') return false;
  if (msg['rttMs'] !== undefined && typeof msg['rttMs'] !== 'number') return false;
  if (msg['jbMs'] !== undefined && typeof msg['jbMs'] !== 'number') return false;
  return true;
}

/** Guest → host: rate-limited proof of player input (kb/mouse/touch/gamepad). */
export const PLAYER_ACTIVITY_MESSAGE_TYPE = 'playerActivity' as const;

export type PlayerActivityMessage = {
  type: typeof PLAYER_ACTIVITY_MESSAGE_TYPE;
};

export function isPlayerActivityMessage(value: unknown): value is PlayerActivityMessage {
  if (value === null || typeof value !== 'object') return false;
  const msg = value as Record<string, unknown>;
  return msg['type'] === PLAYER_ACTIVITY_MESSAGE_TYPE;
}

/** Guest must be idle this long before bitrate climb / climb-IDR is allowed. */
export const RECOVERY_IDLE_BEFORE_CLIMB_MS = 5_000;

/** Guest rate-limit for playerActivity datachannel sends. */
export const PLAYER_ACTIVITY_MIN_INTERVAL_MS = 250;

/**
 * Fail-closed interaction gate for hitchy recovery (climb + IDR).
 * If the host has never received activity, treat as interacting so climb IDR is never risked
 * without proof of idle.
 */
export function isPlayerInteracting(
  lastActivityAtMs: number | null,
  nowMs: number,
  hasEverReceivedActivity: boolean,
  idleBeforeClimbMs: number = RECOVERY_IDLE_BEFORE_CLIMB_MS,
): boolean {
  if (!hasEverReceivedActivity) return true;
  if (lastActivityAtMs === null) return true;
  return nowMs - lastActivityAtMs < idleBeforeClimbMs;
}

/**
 * Host recovery cut when congestion is critical (bps).
 *
 * Recovery never downscales resolution and never lowers framerate: bitrate is the only
 * actuator. A hard cut is a deep bitrate cut held at TARGET_FRAMERATE.
 */
export const RECOVERY_MAX_BITRATE_BPS = 1_500_000;

/**
 * How long the host keeps the match/stream alive after the last player drops so
 * the guest can reclaim signaling and re-offer (WiFi toggle / brief outage).
 *
 * Sized to just outlast the guest's ENTIRE recovery budget - the ICE grace, both reclaim
 * attempts (including onBeforeReconnect waits and watchdog timeouts) and inter-attempt
 * delays - and nothing more. Recovery either lands inside this window or it has failed.
 *
 * Must strictly exceed GUEST_ICE_DISCONNECT_GRACE_MS + GUEST_TOTAL_RECOVERY_BUDGET_MS.
 * The hold starts when the transport drops, but the guest's budget does not start until the
 * blip grace has expired, so the guest can legitimately still be recovering at grace+budget.
 * Sizing this against the budget alone leaves a window where a guest that recovers at 12s
 * arrives after the hold lapsed, reads as a stranger, and loses the tier demotion and the
 * seat it spent those twelve seconds trying to keep. That ordering is the contract between
 * the two sides, and it is why raising either guest window raises this with it.
 */
export const HOST_PLAYER_RECONNECT_HOLD_MS = 15_000;

/**
 * Cap on GuestPeerSession retryDelayMs (matches marketing-demo / beacon retryPolicy).
 *
 * Backoff only. It used to double as the ICE-reclaim wait, which quietly tied the window
 * for receiving refreshed TURN credentials to the retry ladder's pacing - so shortening the
 * ladder shortened the credential wait with it. See GUEST_ICE_REFRESH_TIMEOUT_MS.
 *
 * An interactive stream has roughly three seconds of frozen frame before the session is
 * lost regardless of what happens next, so a reclaim that waits longer than this has
 * already spent the budget it was trying to save.
 */
export const GUEST_RECONNECT_RETRY_CAP_MS = 1_000;

/**
 * Reclaim attempts a guest makes before giving up and refreshing the connection outright.
 *
 * Deliberately small. A reclaim that is going to work works on the first or second try:
 * the failure modes it addresses (WiFi->cellular NAT change, a brief radio outage, a
 * signaling blip) are all resolved by the time the second attempt issues. Beyond that the
 * attempts are just latency in front of the thing that actually recovers the session - a
 * full rebuild - while the player stares at a frozen frame.
 */
export const GUEST_RECONNECT_MAX_ATTEMPTS = 2;

/**
 * How long a reclaim attempt may show NO ICE progress before it counts as failed.
 *
 * A reclaim resolves as soon as `connect()` has issued the offer; whether the new transport
 * ever reaches ICE-connected is signalled only by later events. Without this bound, an
 * attempt whose PC never leaves `new` leaves the guest on the reconnecting spinner
 * indefinitely, because nothing re-arms the retry ladder - the ICE-disconnect grace only
 * applies to a connection that was previously up.
 *
 * It is a NO-PROGRESS bound, not a wall-clock one, which is what lets it stay this short.
 * Relay (TURN) negotiation on a cellular link can legitimately take seconds, and killing an
 * attempt that is actively checking would burn the ladder on healthy-but-slow paths; the
 * watchdog therefore re-arms while ICE is still advancing and only fails an attempt that is
 * going nowhere. GUEST_RECOVERY_DEADLINE_MS is what bounds the slow-but-live case.
 */
export const GUEST_RECONNECT_ATTEMPT_TIMEOUT_MS = 700;

/**
 * When a recovery that is getting NOWHERE gives up and hands over to the full refresh,
 * measured from the moment the transport was lost.
 *
 * An interactive stream is gone to the player after roughly this much frozen frame, so this
 * is the product constraint the ladder serves: three seconds of no progress is the whole
 * budget, and past it the refresh is the only thing left worth doing.
 *
 * It deliberately does NOT cut short an attempt whose ICE is actively negotiating. Doing so
 * looks like enforcing the ceiling and is strictly counterproductive: the refresh behind it
 * has to redo the very same relay negotiation from scratch, so abandoning a live one cannot
 * produce a picture sooner - it only guarantees a later one. A slow cellular TURN path is
 * bounded by GUEST_RECOVERY_HARD_CEILING_MS instead.
 */
export const GUEST_RECOVERY_DEADLINE_MS = 2_000;

/**
 * The WHOLE recovery episode, measured from the moment the transport was lost: every
 * in-place reclaim attempt, at every layer, until the guest gives up and refreshes.
 *
 * This is an EPISODE budget, not a per-ladder one, and that distinction is the entire point.
 * Bounding each layer separately produced ~33s of frozen frame in the worst case: one 8s
 * ladder, then a refresh that reset the counter for a fresh 8s ladder, then two more
 * app-level rebuilds each with their own. A player is long gone by then.
 *
 * Hidden time does not count against it — a backgrounded tab is not a waiting user. See
 * ./guestSessionLifecycle (SuspendableClock) for the clock that enforces that.
 */
export const GUEST_TOTAL_RECOVERY_BUDGET_MS = 6_000;

/**
 * Absolute ceiling on being STUCK, after which the app reloads the page.
 *
 * Every other bound here governs a path that reports back — a reclaim that fails, a probe that
 * answers, a transport that gives up. This one exists for rebuild/refresh paths that do not:
 * a rebuilt session calls connect() directly rather than through runReconnect, so it never
 * arms the attempt watchdog and can leave the guest on "recovering lost connection" with
 * nothing left to fire.
 *
 * Armed only when recovery begins (reclaim retry / rebuild). Cold join does not arm it —
 * that path is bounded by GUEST_PEER_ID_TIMEOUT_MS instead. Rebuilds keep the signaling
 * peer id, so the two timers are not racing each other.
 *
 * A page reload is a blunt instrument and deliberately last: it only fires after the
 * connection refresh has been given a chance. Five seconds is long enough for a healthy
 * rebuild to land, and short enough that a wedged "recovering" spinner does not linger.
 */
export const GUEST_STUCK_RECOVERY_RELOAD_MS = 5_000;

/**
 * How long to wait for the signaling server to answer a PEER_GET_ID before giving up.
 *
 * The peer id is fetched by sending a message over the realtime websocket and waiting for the
 * answer to land in a store listener. When the network is gone — a phone that just lost its
 * signal — the message goes nowhere, the answer never arrives, and that listener waits
 * forever. Nothing downstream can help, because the whole session is parked before a peer
 * connection has been built at all.
 *
 * Bounding it turns "connecting, indefinitely" into a real outcome the app can render. Sized
 * well above a slow-but-working round trip so a congested link still connects. This timer is
 * for cold join (no peer id yet). It is not ordered against GUEST_STUCK_RECOVERY_RELOAD_MS —
 * rebuild/rejoin keeps the signaling peer id and skips PEER_GET_ID, so the stuck-reload
 * path never waits on this bound.
 */
export const GUEST_PEER_ID_TIMEOUT_MS = 8_000;

/**
 * Absolute stop for a recovery, however healthy it still looks.
 *
 * ICE can sit in `checking` indefinitely, so "still progressing" cannot be an open-ended
 * licence to wait.
 *
 * Now derived from the EPISODE budget rather than from the host hold. Deriving it from the
 * hold made it a per-ladder ceiling that reset with every refresh, so the number a player
 * actually experienced was some multiple of it that nothing bounded; deriving it from the
 * total makes the ceiling and the product promise the same number.
 */
export const GUEST_RECOVERY_HARD_CEILING_MS = GUEST_TOTAL_RECOVERY_BUDGET_MS;

/**
 * How long ICE may sit in `disconnected` before the guest escalates to a full reclaim.
 *
 * Part of the same few-second budget: an interactive stream is lost to the player long
 * before a longer wait pays off, and the in-place restart below already covers the blips
 * that heal on their own.
 */
/**
 * How long a reclaim waits for signaling to answer with refreshed ICE servers.
 *
 * This is the TURN credential window, and it is a signaling round trip, not a backoff: on
 * the cellular failover relay exists to survive, the answer routinely takes longer than the
 * gap between two retries. Reconnecting without it means presenting cached credentials on a
 * new NAT path, which is where relay fails and takes the session with it.
 *
 * Generous on purpose, and safe to be: the reclaim's pre-offer waits are raced against the
 * recovery budget, so this cannot extend recovery past its ceiling on its own.
 */
export const GUEST_ICE_REFRESH_TIMEOUT_MS = 2_500;

export const GUEST_ICE_DISCONNECT_GRACE_MS = 2_000;

/**
 * How long ICE may sit in `disconnected` before an in-place restart is attempted.
 *
 * Cheap and non-disruptive - no renegotiation, no rebuild, no camera churn - so it runs
 * early and is the path most brief outages recover through.
 */
export const GUEST_ICE_EARLY_RESTART_MS = 1_000;

/**
 * How long inbound video may sit still before the path is rebuilt.
 *
 * A cellular-to-Wi-Fi change leaves ICE `connected` and loss at 0%. The picture is already
 * gone; waiting for `disconnected` is what made the guest refresh the page. Three seconds
 * is long enough to ignore a single late sample and short enough that the browser's
 * bandwidth estimate has not yet collapsed the encode.
 */
export const GUEST_PATH_SILENCE_MS = 3_000;

/**
 * The guest's whole recovery budget, worst case: the ICE grace before recovery even starts,
 * then the hard deadline the ladder runs under.
 *
 * The ladder no longer sums per-attempt timers, because that arithmetic is what pushed the
 * budget out to tens of seconds: every attempt bound and inter-attempt delay had to be
 * generous enough for the worst path, and the total was whatever fell out. The deadline
 * inverts it - the total is fixed at what a player will actually wait, and the attempts
 * live inside it.
 */
export const GUEST_RECOVERY_BUDGET_MS =
  GUEST_ICE_DISCONNECT_GRACE_MS + GUEST_RECOVERY_DEADLINE_MS;

/**
 * How long a recovered connection must hold before the attempt budget is refunded.
 *
 * The counter used to reset the instant ICE and the data channel came up, which meant a
 * flapping link - up for a second, down again - never accumulated attempts and retried
 * forever. Recovery only counts once the connection has actually survived a while.
 */
export const GUEST_CONNECTION_STABLE_MS = 5_000;

/**
 * How long a hidden document may stay hidden before the guest stops treating the session
 * as recoverable in place and prepares a clean rejoin on return. Generous: the cost of
 * waiting is nothing while hidden, and the user is not watching.
 */
export const GUEST_HIDDEN_SESSION_HOLD_MS = 300_000;

/**
 * Grace after the document becomes visible again before recovery decisions are made,
 * so a resumed page is not judged on transport state sampled mid-wake.
 */
export const GUEST_RESUME_SETTLE_MS = 750;

/**
 * Last-resort guest/join UI timeout (host hold + one reclaim wait).
 * Primary rule: do not show a failure CTA while GuestPeerSession is still
 * reconnecting (`isReconnectInProgress()` / connection status `reconnecting`).
 * This cap only applies once PeerCore is no longer retrying.
 */
export const GUEST_RECONNECT_UI_TIMEOUT_MS =
  HOST_PLAYER_RECONNECT_HOLD_MS + GUEST_RECONNECT_RETRY_CAP_MS;

/**
 * Host → guest: phone-style signal quality for the stream HUD.
 *
 * The bars read the PLAYER-VISIBLE LATENCY — the calibrated glass-to-glass number — not the
 * encoder's recovery state. For gaming the figure that matters is how long the player waits
 * between acting and seeing the frame that shows the reaction; the HUD is a coarse reading
 * of that. `none` means no reading exists yet, and the guest draws no bars for it.
 */
export const STREAM_SIGNAL_QUALITY_MESSAGE_TYPE = 'streamSignalQuality' as const;

export type StreamSignalQualityLevel = 'ideal' | 'degraded' | 'worst' | 'none';

/** Player-visible latency at or below this (ms) is green, three bars. */
export const SIGNAL_LATENCY_GREEN_MS = 30;
/** Player-visible latency at or below this (ms) is yellow, two bars; above it red, one bar. */
export const SIGNAL_LATENCY_YELLOW_MS = 100;

export type StreamSignalQualityMessage = {
  type: typeof STREAM_SIGNAL_QUALITY_MESSAGE_TYPE;
  level: StreamSignalQualityLevel;
};

export function isStreamSignalQualityMessage(value: unknown): value is StreamSignalQualityMessage {
  if (value === null || typeof value !== 'object') return false;
  const msg = value as Record<string, unknown>;
  if (msg['type'] !== STREAM_SIGNAL_QUALITY_MESSAGE_TYPE) return false;
  const level = msg['level'];
  return level === 'ideal' || level === 'degraded' || level === 'worst' || level === 'none';
}

/**
 * Map a measured player-visible latency to the HUD level: ≤ SIGNAL_LATENCY_GREEN_MS is
 * `ideal`, ≤ SIGNAL_LATENCY_YELLOW_MS is `degraded`, above that `worst`. A non-finite or
 * negative value is not a reading and maps to `none`.
 */
export function signalLevelForLatencyMs(latencyMs: number): StreamSignalQualityLevel {
  if (!Number.isFinite(latencyMs) || latencyMs < 0) return 'none';
  if (latencyMs <= SIGNAL_LATENCY_GREEN_MS) return 'ideal';
  if (latencyMs <= SIGNAL_LATENCY_YELLOW_MS) return 'degraded';
  return 'worst';
}

/**
 * How long continued critical congestion must persist before the episode is marked
 * `escalated`. Escalation is a state/telemetry distinction only — the applied encoding
 * is the same hard cut, because deepening the response would mean downscaling.
 */
export const RECOVERY_ESCALATE_AFTER_MS = 3_000;

export const RECOVERY_HEALTHY_HOLD_MS = 5_000;

/**
 * Recovery triggers measured as excess queueing above the peer's own min-filtered RTT
 * floor, never as absolute RTT. A guest 180ms away on a clean link has a 180ms floor and
 * zero standing queue, and must never be treated as congested.
 *
 * Absolute thresholds could not express that difference: 150ms of RTT is a congested LAN
 * peer and a healthy trans-continental one at the same time, so the distant guest entered
 * recovery on connect and — because an 80ms absolute exit is unreachable when propagation
 * alone costs 180ms — could never leave it again.
 *
 * UNVALIDATED ESTIMATES. Hysteresis ordering is the part that is load-bearing and must
 * hold: every exit sits strictly below its own enter, and the sustained enter sits below
 * the instantaneous enter so spikes still trip the fast path first.
 *
 * Floor measurement lives in ./rttFloorTracker (RTT_FLOOR_WINDOW_MS / RTT_FLOOR_MIN_SAMPLES).
 * Packet loss stays an absolute trigger — loss is not distance-dependent.
 *
 * sendDelayMs / outbound-rtp totalPacketSendDelay MUST NOT enter recovery
 * (pacer delay scales with packets-per-frame and false-triggers under healthy load).
 */
export const RECOVERY_STANDING_QUEUE_MS_ENTER = 80;
export const SUSTAINED_STANDING_QUEUE_MS_ENTER = 30;
export const RECOVERY_STANDING_QUEUE_MS_EXIT = 15;
export const RECOVERY_HARD_STANDING_QUEUE_MS_EXIT = 25;

/**
 * Dwell the sustained standing queue must hold before an elevated (soft) recovery
 * escalates to the hard cut. On mobile, low-latency mode pins jitterBufferTarget to 0 so
 * jitter thresholds rarely trip, leaving queueing as the only reliable sustained signal.
 * Short spikes reset the dwell.
 */
export const SUSTAINED_RTT_WINDOW_MS = 3_000;

/** Elevated soft-cut: fraction of profile maxBitrate. */
export const RECOVERY_ELEVATED_BITRATE_FRACTION = 0.6;

/**
 * Compute elevated soft-cut max bitrate from profile max (bps).
 * Round to nearest integer; clamp to at least 1.
 */
export function elevatedSoftCutMaxBitrateBps(profileMaxBitrateBps: number): number {
  if (!Number.isFinite(profileMaxBitrateBps) || profileMaxBitrateBps <= 0) {
    return 1;
  }
  return Math.max(1, Math.round(profileMaxBitrateBps * RECOVERY_ELEVATED_BITRATE_FRACTION));
}

/**
 * Within-tier recovery encoding (decision 4C). Soft/hard adjust fps + bitrate only;
 * scaleResolutionDownBy is always 1 — callers must not change scale or ShadowCam mid-session.
 */
export type WithinTierEncoding = {
  maxBitrateBps: number;
  maxFramerate: number;
  /** Always 1 — callers must not change scale for tier/recovery (4C). */
  scaleResolutionDownBy: 1;
};

/** Soft = fraction of profile max @ TARGET_FRAMERATE; resolution-pinned. */
export function softCutEncoding(profileMaxBitrateBps: number): WithinTierEncoding {
  return {
    maxBitrateBps: elevatedSoftCutMaxBitrateBps(profileMaxBitrateBps),
    maxFramerate: TARGET_FRAMERATE,
    scaleResolutionDownBy: PINNED_SCALE_RESOLUTION_DOWN_BY,
  };
}

/**
 * Hard = RECOVERY_MAX_BITRATE_BPS @ TARGET_FRAMERATE; resolution-pinned.
 *
 * Framerate is not a congestion actuator. Halving cadence is the most visible thing a
 * stream can do to an interactive session, and it buys far less than the bitrate cut
 * beside it, so the hard cut holds 60fps and moves bitrate alone.
 */
export function hardCutEncoding(): WithinTierEncoding {
  return {
    maxBitrateBps: RECOVERY_MAX_BITRATE_BPS,
    maxFramerate: TARGET_FRAMERATE,
    scaleResolutionDownBy: PINNED_SCALE_RESOLUTION_DOWN_BY,
  };
}

/**
 * Hard-cut bitrate for a peer whose profile max is `profileMaxBitrateBps` (bps).
 *
 * RECOVERY_MAX_BITRATE_BPS is an absolute target chosen against the usual multi-Mbps
 * profile, and it stops meaning "a deep cut" once the operator ceiling reaches down to
 * it. Taking the ceiling itself would write an encoder cap the stream already had, so
 * critical congestion would get no mitigation at all; the soft-cut fraction is the floor
 * that keeps the cut real. Hard therefore lands at or below soft for every ceiling,
 * which is also what stops a soft→hard escalation from raising the cap.
 */
export function hardCutBitrateBps(profileMaxBitrateBps: number): number {
  return Math.min(RECOVERY_MAX_BITRATE_BPS, elevatedSoftCutMaxBitrateBps(profileMaxBitrateBps));
}

/** Client signal rate limit. */
export const CLIENT_CONGESTION_MIN_INTERVAL_MS = 2_000;

/**
 * Host: minimum ms between honoring client `requestKeyframe` messages.
 * Startup / recovery IDRs owned by the host are not subject to this limit.
 */
export const HOST_CLIENT_KEYFRAME_MIN_INTERVAL_MS = 2_000;

/**
 * At most one IDR per climb episode when idle+safe recovery is allowed.
 * Recovery *enter* (soft/hard bitrate cut) must NOT request an IDR — that hitch is reserved
 * for quiet moments after the player stops interacting.
 */
export const RECOVERY_KEYFRAME_ONCE = true;

export type HardwareAccelerationKind = 'hardware' | 'software' | 'unknown';

export type HardwareCodecProbeResult = {
  encode: HardwareAccelerationKind;
  decode: HardwareAccelerationKind;
  encoderImplementation?: string;
  decoderImplementation?: string;
  /** What was actually negotiated, e.g. `video/H264 42e01f`. Diagnostic only. */
  negotiatedVideoCodec?: string;
  /** H.264 profiles the guest OFFER carried - the ceiling any host-side fix can reach. */
  offeredVideoProfiles?: string[];
  /** Compact result of the last send-codec pin attempt, e.g. `pinned:42001f@after-answer`. */
  sendCodecPin?: string;
};

/**
 * Tested against the lower-cased implementation name.
 *
 * `soft` is anchored to the start of a word: as a bare substring it also matches
 * "Microsoft", so every Microsoft-branded encoder was classified as software and
 * raised a hardware-codec warning banner plus an ICE restart on hardware sessions.
 */
const SOFTWARE_IMPL_PATTERNS: RegExp[] = [
  /openh264/,
  /libvpx/,
  /libaom/,
  /ffmpeg/,
  /software/,
  /sw_/,
  /(?:^|[^a-z])soft/,
];

const HARDWARE_IMPL_PATTERNS: RegExp[] = [
  /externalencoder/,
  /d3d11/,
  /videotoolbox/,
  /mediacodec/,
  /vaapi/,
  /nvenc/,
  /qsv/,
  /quick sync/,
  /v4l2/,
  /mediafoundation/,
];

/**
 * Classify Chromium getStats implementation strings.
 * Known SW examples: OpenH264, libvpx, libaom, FFmpeg, Software.
 * Known HW examples: ExternalEncoder, D3D11*, VideoToolbox, MediaCodec, Vaapi.
 *
 * Hardware is tested first: vendor branding in a hardware implementation name is a
 * likelier source of an accidental software match than the reverse.
 */
export function classifyImplementationName(name: string | undefined): HardwareAccelerationKind {
  if (!name || !name.trim()) return 'unknown';
  const lower = name.toLowerCase();
  if (HARDWARE_IMPL_PATTERNS.some((p) => p.test(lower))) return 'hardware';
  if (SOFTWARE_IMPL_PATTERNS.some((p) => p.test(lower))) return 'software';
  return 'unknown';
}

/**
 * SLO: a dropped transport must resume media within this long (ms).
 *
 * A broken connection is a bug if it waits forever or times out slowly. The guest recovery
 * ladder is tuned toward this target; the harness gates on it. Measured drop-start →
 * media-resumed (see GuestRecoveryEpisode).
 */
export const RESUME_AFTER_DROPOUT_SLO_MS = 2_000;

/**
 * Hard ceiling after which a non-resumed session is a bug, not a retry (ms).
 * A resume that exceeds this is a defect regardless of the ladder's internal state.
 */
export const RESUME_AFTER_DROPOUT_HARD_MAX_MS = 4_000;

/**
 * Host-facing calibrated latency summary for HUD / beacon / climb gate.
 * `source` is required so sinks never treat an untagged sample as authoritative.
 */
export type CalibratedLatencySummary = {
  freshness?: MeasurementFreshness;
  timingSource?: FrameTimingSource;
  lastMs: number | null;
  p50Ms: number | null;
  p95Ms: number | null;
  /**
   * The link's latency floor (window minimum): propagation plus the fixed pipeline, the part
   * no encoder setting can change. Null until enough samples make it trustworthy.
   */
  floorMs: number | null;
  /** The latest reading above the floor — the part a cut can act on. Null while the floor is untrusted. */
  excessMs: number | null;
  sampleCount: number;
  offsetTrusted: boolean;
  /** How the latest correlated report was produced. Null before any tagged report. */
  source: LatencyProbeSource | null;
};
